import { Link, useLocation } from "react-router-dom";
import { CareShell, Panel } from "../../components/care/CareLayout";
import {
  ncpAssessmentFields,
  ncpExistingClientSteps,
  ncpNewClientSteps,
  ncpObservationNotes,
  ncpScreens,
} from "./careData";

const screenDetails = (screen) => {
  const [eyebrow, title, description, status, cta, ctaPath] = ncpScreens[screen] || ncpScreens.overview;
  return { eyebrow, title, description, status, cta, ctaPath };
};

const stepTone = (state) => (state === "active" ? "blue" : state === "review" ? "purple" : "low");

export default function NegotiatedCarePlanPage({ screen = "overview" }) {
  const details = screenDetails(screen);
  const { pathname } = useLocation();

  return (
    <CareShell className="dashboard-content ncp-flow">
      <div className="crumbs">Care Plans <i className="ti ti-chevron-right" /> Negotiated Care Plan</div>
      <div className="ncp-hero">
        <div>
          <span className="section-eyebrow">{details.eyebrow}</span>
          <h1>{details.title}</h1>
          <p>{details.description}</p>
        </div>
        <div className="ncp-hero-actions">
          <em>{details.status}</em>
          <Link className="btn primary" to={details.ctaPath}>{details.cta}</Link>
        </div>
      </div>

      <div className="ncp-status-grid">
        {[
          ["ti-file-description", "Assessment", "State assessment imported", "Complete"],
          ["ti-stethoscope", "MaG Evaluation", "Provider review in progress", "Review"],
          ["ti-notes", "30-Day Notes", "18 notes captured", "Active"],
          ["ti-file-check", "State Confirmation", "Waiting on final files", "Pending"],
        ].map(([icon, label, text, status]) => (
          <article className="ncp-status-card" key={label}>
            <span className="tiny-icon"><i className={`ti ${icon}`} /></span>
            <div><h3>{label}</h3><p>{text}</p></div>
            <em>{status}</em>
          </article>
        ))}
      </div>

      <div className="ncp-screen-grid">
        <Panel title="New Client Flow">
          <div className="ncp-step-list">
            {ncpNewClientSteps.map(([number, title, text, path, state]) => (
              <Link className={`ncp-step ${path === pathname ? "is-active" : ""}`} to={path} key={`${number}-${title}`}>
                <b>{number}</b><span><strong>{title}</strong><small>{text}</small></span><em className={`pill ${stepTone(state)}`}>{state}</em>
              </Link>
            ))}
          </div>
        </Panel>
        <Panel title="Existing Client Flow">
          <div className="ncp-step-list compact">
            {ncpExistingClientSteps.map(([number, title, text, path, state]) => (
              <Link className={`ncp-step ${path === pathname ? "is-active" : ""}`} to={path} key={`${number}-${title}`}>
                <b>{number}</b><span><strong>{title}</strong><small>{text}</small></span><em className={`pill ${stepTone(state)}`}>{state}</em>
              </Link>
            ))}
          </div>
        </Panel>
      </div>

      <div className="ncp-detail-grid">
        <Panel title={screen === "annual" ? "Yearly Assessment Inputs" : "Assessment and MaG Inputs"}>
          <div className="ncp-field-grid">
            {ncpAssessmentFields.map(([icon, label, text]) => (
              <article className="ncp-field-card" key={label}>
                <span className="round-icon blue"><i className={`ti ${icon}`} /></span>
                <h3>{label}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </Panel>

        <Panel title={screen === "confirmed" ? "Confirmed Files" : "Care Plan Workspace"}>
          {screen === "confirmed" ? (
            <div className="ncp-file-list">
              {["Confirmed Negotiated Care Plan.pdf", "Updated State Assessment.pdf", "Pay Negotiation Agreement.pdf"].map((file) => (
                <a className="ncp-file-row" href="#download" key={file}><i className="ti ti-file-download" /><span>{file}</span><strong>Download</strong></a>
              ))}
            </div>
          ) : (
            <div className="ncp-workspace-card">
              <div className="ncp-template-switch"><button className="mini-btn edit">State Template</button><button className="mini-btn">Provider Template</button></div>
              <label>Provider final inputs<textarea defaultValue="Add final changes from observed behavior, health, welfare, family input, and care team updates before sending to the social worker." /></label>
              <div className="ncp-negotiation-row"><span><strong>Social worker negotiation</strong><small>Pay level discussion ready</small></span><button className="mini-btn"><i className="ti ti-phone" /> Contact</button></div>
            </div>
          )}
        </Panel>
      </div>

      <Panel title={screen === "probation" ? "30-Day Observation Notes" : "Ongoing Notes and Care Team Updates"}>
        <div className="ncp-note-grid">
          {ncpObservationNotes.map(([label, text]) => <article className="ncp-note-card" key={label}><strong>{label}</strong><p>{text}</p></article>)}
        </div>
        <div className="ncp-team-row">
          {["PCP", "Physical Therapist", "Occupational Therapist", "PoA / Family", "Social Worker"].map((member) => <span key={member}><i className="ti ti-user-check" /> {member}</span>)}
        </div>
      </Panel>
    </CareShell>
  );
}
